export interface CountryCode {
  code: string;
  country: string;
  iso: string;
  length: number;
  placeholder: string;
}

const buildPlaceholder = (len: number) => 'X'.repeat(len);

// Every entry has the dial code, country name, ISO 3166-1 alpha-2 (for the
// flag icon), the expected mobile-number length, and a placeholder built from
// the length. Tunisia is first so it stays the default for this app.
const RAW_COUNTRY_CODES: Array<Omit<CountryCode, 'placeholder'>> = [
  { code: '+216', country: 'Tunisia', iso: 'tn', length: 8 },
  { code: '+33', country: 'France', iso: 'fr', length: 9 },
  { code: '+213', country: 'Algeria', iso: 'dz', length: 9 },
  { code: '+212', country: 'Morocco', iso: 'ma', length: 9 },
  { code: '+44', country: 'United Kingdom', iso: 'gb', length: 10 },
  { code: '+1', country: 'USA/Canada', iso: 'us', length: 10 },
  { code: '+49', country: 'Germany', iso: 'de', length: 10 },
  { code: '+39', country: 'Italy', iso: 'it', length: 10 },
  { code: '+34', country: 'Spain', iso: 'es', length: 9 },
  { code: '+91', country: 'India', iso: 'in', length: 10 },
  { code: '+966', country: 'Saudi Arabia', iso: 'sa', length: 9 },
  { code: '+971', country: 'UAE', iso: 'ae', length: 9 },
  { code: '+90', country: 'Turkey', iso: 'tr', length: 10 },
  { code: '+20', country: 'Egypt', iso: 'eg', length: 10 },
  { code: '+218', country: 'Libya', iso: 'ly', length: 9 },
  { code: '+974', country: 'Qatar', iso: 'qa', length: 8 },
  { code: '+965', country: 'Kuwait', iso: 'kw', length: 8 },
  { code: '+968', country: 'Oman', iso: 'om', length: 8 },
  { code: '+973', country: 'Bahrain', iso: 'bh', length: 8 },
  { code: '+962', country: 'Jordan', iso: 'jo', length: 9 },
  { code: '+961', country: 'Lebanon', iso: 'lb', length: 8 },
  { code: '+972', country: 'Israel', iso: 'il', length: 9 },
  { code: '+970', country: 'Palestine', iso: 'ps', length: 9 },
  { code: '+963', country: 'Syria', iso: 'sy', length: 9 },
  { code: '+964', country: 'Iraq', iso: 'iq', length: 10 },
  { code: '+98', country: 'Iran', iso: 'ir', length: 10 },
  { code: '+967', country: 'Yemen', iso: 'ye', length: 9 },
  { code: '+41', country: 'Switzerland', iso: 'ch', length: 9 },
  { code: '+32', country: 'Belgium', iso: 'be', length: 9 },
  { code: '+31', country: 'Netherlands', iso: 'nl', length: 9 },
  { code: '+43', country: 'Austria', iso: 'at', length: 10 },
  { code: '+46', country: 'Sweden', iso: 'se', length: 9 },
  { code: '+47', country: 'Norway', iso: 'no', length: 8 },
  { code: '+45', country: 'Denmark', iso: 'dk', length: 8 },
  { code: '+358', country: 'Finland', iso: 'fi', length: 9 },
  { code: '+351', country: 'Portugal', iso: 'pt', length: 9 },
  { code: '+30', country: 'Greece', iso: 'gr', length: 10 },
  { code: '+353', country: 'Ireland', iso: 'ie', length: 9 },
  { code: '+356', country: 'Malta', iso: 'mt', length: 8 },
  { code: '+357', country: 'Cyprus', iso: 'cy', length: 8 },
  { code: '+352', country: 'Luxembourg', iso: 'lu', length: 9 },
  { code: '+377', country: 'Monaco', iso: 'mc', length: 8 },
  { code: '+376', country: 'Andorra', iso: 'ad', length: 6 },
  { code: '+378', country: 'San Marino', iso: 'sm', length: 10 },
  { code: '+379', country: 'Vatican City', iso: 'va', length: 10 },
  { code: '+423', country: 'Liechtenstein', iso: 'li', length: 7 },
  { code: '+354', country: 'Iceland', iso: 'is', length: 7 },
  { code: '+298', country: 'Faroe Islands', iso: 'fo', length: 6 },
  { code: '+299', country: 'Greenland', iso: 'gl', length: 6 },
  { code: '+7', country: 'Russia', iso: 'ru', length: 10 },
  { code: '+375', country: 'Belarus', iso: 'by', length: 9 },
  { code: '+380', country: 'Ukraine', iso: 'ua', length: 9 },
  { code: '+48', country: 'Poland', iso: 'pl', length: 9 },
  { code: '+40', country: 'Romania', iso: 'ro', length: 9 },
  { code: '+36', country: 'Hungary', iso: 'hu', length: 9 },
  { code: '+420', country: 'Czech Republic', iso: 'cz', length: 9 },
  { code: '+421', country: 'Slovakia', iso: 'sk', length: 9 },
  { code: '+359', country: 'Bulgaria', iso: 'bg', length: 9 },
  { code: '+385', country: 'Croatia', iso: 'hr', length: 9 },
  { code: '+381', country: 'Serbia', iso: 'rs', length: 9 },
  { code: '+386', country: 'Slovenia', iso: 'si', length: 8 },
  { code: '+370', country: 'Lithuania', iso: 'lt', length: 8 },
  { code: '+371', country: 'Latvia', iso: 'lv', length: 8 },
  { code: '+372', country: 'Estonia', iso: 'ee', length: 8 },
  { code: '+382', country: 'Montenegro', iso: 'me', length: 8 },
  { code: '+383', country: 'Kosovo', iso: 'xk', length: 8 },
  { code: '+355', country: 'Albania', iso: 'al', length: 9 },
  { code: '+387', country: 'Bosnia and Herzegovina', iso: 'ba', length: 8 },
  { code: '+389', country: 'North Macedonia', iso: 'mk', length: 8 },
  { code: '+373', country: 'Moldova', iso: 'md', length: 8 },
  { code: '+374', country: 'Armenia', iso: 'am', length: 8 },
  { code: '+994', country: 'Azerbaijan', iso: 'az', length: 9 },
  { code: '+995', country: 'Georgia', iso: 'ge', length: 9 },
  { code: '+998', country: 'Uzbekistan', iso: 'uz', length: 9 },
  { code: '+992', country: 'Tajikistan', iso: 'tj', length: 9 },
  { code: '+993', country: 'Turkmenistan', iso: 'tm', length: 8 },
  { code: '+996', country: 'Kyrgyzstan', iso: 'kg', length: 9 },
  { code: '+7', country: 'Kazakhstan', iso: 'kz', length: 10 },
  { code: '+976', country: 'Mongolia', iso: 'mn', length: 8 },
  { code: '+81', country: 'Japan', iso: 'jp', length: 10 },
  { code: '+86', country: 'China', iso: 'cn', length: 11 },
  { code: '+82', country: 'South Korea', iso: 'kr', length: 10 },
  { code: '+850', country: 'North Korea', iso: 'kp', length: 10 },
  { code: '+852', country: 'Hong Kong', iso: 'hk', length: 8 },
  { code: '+853', country: 'Macau', iso: 'mo', length: 8 },
  { code: '+886', country: 'Taiwan', iso: 'tw', length: 9 },
  { code: '+60', country: 'Malaysia', iso: 'my', length: 9 },
  { code: '+65', country: 'Singapore', iso: 'sg', length: 8 },
  { code: '+66', country: 'Thailand', iso: 'th', length: 9 },
  { code: '+62', country: 'Indonesia', iso: 'id', length: 10 },
  { code: '+63', country: 'Philippines', iso: 'ph', length: 10 },
  { code: '+84', country: 'Vietnam', iso: 'vn', length: 9 },
  { code: '+855', country: 'Cambodia', iso: 'kh', length: 8 },
  { code: '+856', country: 'Laos', iso: 'la', length: 10 },
  { code: '+95', country: 'Myanmar', iso: 'mm', length: 8 },
  { code: '+673', country: 'Brunei', iso: 'bn', length: 7 },
  { code: '+670', country: 'Timor-Leste', iso: 'tl', length: 8 },
  { code: '+92', country: 'Pakistan', iso: 'pk', length: 10 },
  { code: '+880', country: 'Bangladesh', iso: 'bd', length: 10 },
  { code: '+94', country: 'Sri Lanka', iso: 'lk', length: 9 },
  { code: '+977', country: 'Nepal', iso: 'np', length: 10 },
  { code: '+93', country: 'Afghanistan', iso: 'af', length: 9 },
  { code: '+975', country: 'Bhutan', iso: 'bt', length: 8 },
  { code: '+960', country: 'Maldives', iso: 'mv', length: 7 },
  { code: '+61', country: 'Australia', iso: 'au', length: 9 },
  { code: '+64', country: 'New Zealand', iso: 'nz', length: 9 },
  { code: '+679', country: 'Fiji', iso: 'fj', length: 7 },
  { code: '+675', country: 'Papua New Guinea', iso: 'pg', length: 8 },
  { code: '+677', country: 'Solomon Islands', iso: 'sb', length: 7 },
  { code: '+678', country: 'Vanuatu', iso: 'vu', length: 7 },
  { code: '+685', country: 'Samoa', iso: 'ws', length: 7 },
  { code: '+676', country: 'Tonga', iso: 'to', length: 5 },
  { code: '+682', country: 'Cook Islands', iso: 'ck', length: 5 },
  { code: '+683', country: 'Niue', iso: 'nu', length: 4 },
  { code: '+690', country: 'Tokelau', iso: 'tk', length: 4 },
  { code: '+686', country: 'Kiribati', iso: 'ki', length: 8 },
  { code: '+688', country: 'Tuvalu', iso: 'tv', length: 5 },
  { code: '+674', country: 'Nauru', iso: 'nr', length: 7 },
  { code: '+680', country: 'Palau', iso: 'pw', length: 7 },
  { code: '+691', country: 'Micronesia', iso: 'fm', length: 7 },
  { code: '+692', country: 'Marshall Islands', iso: 'mh', length: 7 },
  { code: '+689', country: 'French Polynesia', iso: 'pf', length: 8 },
  { code: '+687', country: 'New Caledonia', iso: 'nc', length: 6 },
  { code: '+681', country: 'Wallis and Futuna', iso: 'wf', length: 6 },
  { code: '+27', country: 'South Africa', iso: 'za', length: 9 },
  { code: '+234', country: 'Nigeria', iso: 'ng', length: 10 },
  { code: '+254', country: 'Kenya', iso: 'ke', length: 9 },
  { code: '+233', country: 'Ghana', iso: 'gh', length: 9 },
  { code: '+251', country: 'Ethiopia', iso: 'et', length: 9 },
  { code: '+256', country: 'Uganda', iso: 'ug', length: 9 },
  { code: '+255', country: 'Tanzania', iso: 'tz', length: 9 },
  { code: '+221', country: 'Senegal', iso: 'sn', length: 9 },
  { code: '+225', country: 'Ivory Coast', iso: 'ci', length: 10 },
  { code: '+237', country: 'Cameroon', iso: 'cm', length: 9 },
  { code: '+241', country: 'Gabon', iso: 'ga', length: 7 },
  { code: '+244', country: 'Angola', iso: 'ao', length: 9 },
  { code: '+260', country: 'Zambia', iso: 'zm', length: 9 },
  { code: '+263', country: 'Zimbabwe', iso: 'zw', length: 9 },
  { code: '+267', country: 'Botswana', iso: 'bw', length: 8 },
  { code: '+264', country: 'Namibia', iso: 'na', length: 9 },
  { code: '+266', country: 'Lesotho', iso: 'ls', length: 8 },
  { code: '+268', country: 'Eswatini', iso: 'sz', length: 8 },
  { code: '+265', country: 'Malawi', iso: 'mw', length: 9 },
  { code: '+258', country: 'Mozambique', iso: 'mz', length: 9 },
  { code: '+250', country: 'Rwanda', iso: 'rw', length: 9 },
  { code: '+257', country: 'Burundi', iso: 'bi', length: 8 },
  { code: '+269', country: 'Comoros', iso: 'km', length: 7 },
  { code: '+253', country: 'Djibouti', iso: 'dj', length: 8 },
  { code: '+291', country: 'Eritrea', iso: 'er', length: 7 },
  { code: '+252', country: 'Somalia', iso: 'so', length: 8 },
  { code: '+249', country: 'Sudan', iso: 'sd', length: 9 },
  { code: '+211', country: 'South Sudan', iso: 'ss', length: 9 },
  { code: '+222', country: 'Mauritania', iso: 'mr', length: 8 },
  { code: '+223', country: 'Mali', iso: 'ml', length: 8 },
  { code: '+226', country: 'Burkina Faso', iso: 'bf', length: 8 },
  { code: '+227', country: 'Niger', iso: 'ne', length: 8 },
  { code: '+235', country: 'Chad', iso: 'td', length: 8 },
  { code: '+236', country: 'Central African Republic', iso: 'cf', length: 8 },
  { code: '+242', country: 'Republic of the Congo', iso: 'cg', length: 9 },
  { code: '+243', country: 'DR Congo', iso: 'cd', length: 9 },
  { code: '+240', country: 'Equatorial Guinea', iso: 'gq', length: 9 },
  { code: '+239', country: 'Sao Tome and Principe', iso: 'st', length: 7 },
  { code: '+238', country: 'Cape Verde', iso: 'cv', length: 7 },
  { code: '+245', country: 'Guinea-Bissau', iso: 'gw', length: 7 },
  { code: '+224', country: 'Guinea', iso: 'gn', length: 9 },
  { code: '+232', country: 'Sierra Leone', iso: 'sl', length: 8 },
  { code: '+231', country: 'Liberia', iso: 'lr', length: 8 },
  { code: '+228', country: 'Togo', iso: 'tg', length: 8 },
  { code: '+229', country: 'Benin', iso: 'bj', length: 8 },
  { code: '+248', country: 'Seychelles', iso: 'sc', length: 7 },
  { code: '+230', country: 'Mauritius', iso: 'mu', length: 7 },
  { code: '+261', country: 'Madagascar', iso: 'mg', length: 9 },
  { code: '+262', country: 'Reunion / Mayotte', iso: 're', length: 9 },
  { code: '+290', country: 'Saint Helena', iso: 'sh', length: 4 },
  { code: '+55', country: 'Brazil', iso: 'br', length: 11 },
  { code: '+54', country: 'Argentina', iso: 'ar', length: 10 },
  { code: '+52', country: 'Mexico', iso: 'mx', length: 10 },
  { code: '+56', country: 'Chile', iso: 'cl', length: 9 },
  { code: '+57', country: 'Colombia', iso: 'co', length: 10 },
  { code: '+51', country: 'Peru', iso: 'pe', length: 9 },
  { code: '+58', country: 'Venezuela', iso: 've', length: 10 },
  { code: '+593', country: 'Ecuador', iso: 'ec', length: 9 },
  { code: '+591', country: 'Bolivia', iso: 'bo', length: 8 },
  { code: '+595', country: 'Paraguay', iso: 'py', length: 9 },
  { code: '+598', country: 'Uruguay', iso: 'uy', length: 8 },
  { code: '+592', country: 'Guyana', iso: 'gy', length: 7 },
  { code: '+597', country: 'Suriname', iso: 'sr', length: 7 },
  { code: '+594', country: 'French Guiana', iso: 'gf', length: 9 },
  { code: '+500', country: 'Falkland Islands', iso: 'fk', length: 5 },
  { code: '+53', country: 'Cuba', iso: 'cu', length: 8 },
  { code: '+509', country: 'Haiti', iso: 'ht', length: 8 },
  { code: '+1', country: 'Dominican Republic', iso: 'do', length: 10 },
  { code: '+1', country: 'Jamaica', iso: 'jm', length: 10 },
  { code: '+1', country: 'Trinidad and Tobago', iso: 'tt', length: 10 },
  { code: '+1', country: 'Puerto Rico', iso: 'pr', length: 10 },
  { code: '+1', country: 'Bahamas', iso: 'bs', length: 10 },
  { code: '+1', country: 'Barbados', iso: 'bb', length: 10 },
  { code: '+1', country: 'Bermuda', iso: 'bm', length: 10 },
  { code: '+1', country: 'Cayman Islands', iso: 'ky', length: 10 },
  { code: '+1', country: 'Anguilla', iso: 'ai', length: 10 },
  { code: '+1', country: 'Antigua and Barbuda', iso: 'ag', length: 10 },
  { code: '+1', country: 'Saint Kitts and Nevis', iso: 'kn', length: 10 },
  { code: '+1', country: 'Saint Lucia', iso: 'lc', length: 10 },
  { code: '+1', country: 'Saint Vincent and the Grenadines', iso: 'vc', length: 10 },
  { code: '+1', country: 'Grenada', iso: 'gd', length: 10 },
  { code: '+1', country: 'Dominica', iso: 'dm', length: 10 },
  { code: '+1', country: 'Montserrat', iso: 'ms', length: 10 },
  { code: '+1', country: 'British Virgin Islands', iso: 'vg', length: 10 },
  { code: '+1', country: 'US Virgin Islands', iso: 'vi', length: 10 },
  { code: '+1', country: 'Turks and Caicos', iso: 'tc', length: 10 },
  { code: '+1', country: 'Sint Maarten', iso: 'sx', length: 10 },
  { code: '+1', country: 'Guam', iso: 'gu', length: 10 },
  { code: '+1', country: 'American Samoa', iso: 'as', length: 10 },
  { code: '+1', country: 'Northern Mariana Islands', iso: 'mp', length: 10 },
  { code: '+297', country: 'Aruba', iso: 'aw', length: 7 },
  { code: '+599', country: 'Curaçao', iso: 'cw', length: 7 },
  { code: '+590', country: 'Guadeloupe', iso: 'gp', length: 9 },
  { code: '+596', country: 'Martinique', iso: 'mq', length: 9 },
  { code: '+501', country: 'Belize', iso: 'bz', length: 7 },
  { code: '+502', country: 'Guatemala', iso: 'gt', length: 8 },
  { code: '+503', country: 'El Salvador', iso: 'sv', length: 8 },
  { code: '+504', country: 'Honduras', iso: 'hn', length: 8 },
  { code: '+505', country: 'Nicaragua', iso: 'ni', length: 8 },
  { code: '+506', country: 'Costa Rica', iso: 'cr', length: 8 },
  { code: '+507', country: 'Panama', iso: 'pa', length: 8 },
  { code: '+350', country: 'Gibraltar', iso: 'gi', length: 8 },
  { code: '+672', country: 'Antarctica / Norfolk', iso: 'aq', length: 6 },
];

export const COUNTRY_CODES: CountryCode[] = RAW_COUNTRY_CODES.map((entry) => ({
  ...entry,
  placeholder: buildPlaceholder(entry.length),
}));

export const DEFAULT_PHONE_COUNTRY = COUNTRY_CODES[0];

/** The country a client's address defaults to; its governorates have their own list. */
export const DEFAULT_ADDRESS_COUNTRY = 'Tunisia';

export interface Country {
  /** ISO 3166-1 alpha-2, lower case (the flag-icons class name). */
  iso: string;
  /** English name: what nationality and address country store. */
  name: string;
  /** How the dial-code list spells it ("USA/Canada", "UAE"), which older records hold. */
  alias?: string;
}

/**
 * Every country of the dial-code list (Canada shares +1 with the USA there, so it is
 * added), named in English by the browser's own country names - no package needed.
 * Tunisia first, then alphabetical.
 */
export const COUNTRIES: Country[] = (() => {
  const names = typeof Intl !== 'undefined' && 'DisplayNames' in Intl
    ? new Intl.DisplayNames(['en'], { type: 'region' })
    : null;
  const byIso = new Map<string, Country>();
  for (const { iso, country } of [...RAW_COUNTRY_CODES, { iso: 'ca', country: 'Canada' }]) {
    if (byIso.has(iso)) continue;
    const name = names?.of(iso.toUpperCase()) || country;
    byIso.set(iso, { iso, name, alias: country !== name ? country : undefined });
  }
  const all = Array.from(byIso.values());
  const home = all.find((entry) => entry.name === DEFAULT_ADDRESS_COUNTRY);
  const rest = all.filter((entry) => entry !== home).sort((a, b) => a.name.localeCompare(b.name));
  return home ? [home, ...rest] : rest;
})();

/** Country names for a client's address, Tunisia first. */
export const ADDRESS_COUNTRIES: string[] = COUNTRIES.map((entry) => entry.name);

/** A stored country name in the reader's language: "Germany" reads "Allemagne" in French. */
export const localCountryName = (value: string | undefined | null, language: string): string => {
  const country = findCountryByName(value);
  if (!country) return value || '';
  try {
    return new Intl.DisplayNames([language], { type: 'region' }).of(country.iso.toUpperCase()) || country.name;
  } catch {
    return country.name;
  }
};

/** The country a stored name refers to, matching its English name or older spelling. */
export const findCountryByName = (value?: string | null): Country | null => {
  const key = (value || '').trim().toLowerCase();
  if (!key) return null;
  return COUNTRIES.find((entry) => entry.name.toLowerCase() === key || entry.alias?.toLowerCase() === key) || null;
};

const PHONE_COUNTRIES_BY_PREFIX_INTERNAL = [...COUNTRY_CODES].sort(
  (a, b) => b.code.length - a.code.length,
);

export const findPhoneCountryByCode = (code: string) =>
  COUNTRY_CODES.find((country) => country.code === code) || DEFAULT_PHONE_COUNTRY;

export const findPhoneCountryByValue = (value: string) =>
  PHONE_COUNTRIES_BY_PREFIX_INTERNAL.find((country) => value.startsWith(country.code));

// Canonicalises a phone string to the `+<dial-code><digits>` form the booking
// form's validator expects. Legacy client/contact records may store phones as
// bare digits ("12345678") or without the leading "+" ("21612345678") — feeding
// those straight into the customer.phone field made react-hook-form reject the
// value and silently blocked the wizard's Next button. Keep this in sync with
// findPhoneCountryByValue in POSCustomerDetailsStep.
export const normalizePhoneValue = (rawPhone?: string | null): string => {
  if (!rawPhone) return '';
  const compact = String(rawPhone).replace(/\s/g, '');
  if (!compact) return '';

  if (findPhoneCountryByValue(compact)) {
    return compact;
  }

  const digits = compact.replace(/^\+/, '').replace(/^00/, '').replace(/\D/g, '');
  if (!digits) return '';

  const matched = PHONE_COUNTRIES_BY_PREFIX_INTERNAL.find((c) => {
    const dial = c.code.slice(1);
    return digits.startsWith(dial) && digits.length === dial.length + c.length;
  });
  if (matched) return `+${digits}`;

  return `${DEFAULT_PHONE_COUNTRY.code}${digits}`;
};
